import { MathHelpers } from "../utils/math";
import { Benchmark } from "../utils/metrics";
import { DeviceScreenRenderingService } from "../services/deviceScreenRenderingService";
import { RcContext } from "./rcContext";
import { DeviceContext } from "./deviceContext";
import { IDeviceCanvasRender, IDeviceContext, IDeviceScreenTransformService } from "../mcrc-api";
import { MouseEventService } from "../services/mouseEventService";

enum AnnotationTool {
    // Pen mode for the annotations feature
    Pen = 1,
    // Eraser mode for the annotations feature
    Eraser = 2
}

export class DeviceCanvas implements IDeviceCanvasRender {
    private benchmark: Benchmark;
    private ctx: DeviceContext;
    private transformService: IDeviceScreenTransformService;

    constructor(
    ) {
        this.benchmark = new Benchmark();
        this.ctx = null;
        this.transformService = null;
    }

    /* istanbul ignore next */
    private DEBUG_debugCanvasDraw(): void {
        DeviceScreenRenderingService.drawTransformedToCanvas(
            this.ctx.canvas.element,
            this.ctx.debugCanvas,
            this.ctx.screenOffsetX,
            this.ctx.screenOffsetY,
            this.ctx.rotation,
            this.ctx.scale,
            this.ctx.targetScale * this.ctx.clientScaleFactor
        );
    }

    /* istanbul ignore next */
    private DEBUG_debugCanvasClearScreen(): void {
        const dbgCanvasContext = this.ctx.debugCanvas.getContext("2d");
        dbgCanvasContext.clearRect(
            0,
            0,
            this.ctx.debugCanvas.width,
            this.ctx.debugCanvas.height
        );
        dbgCanvasContext.beginPath();
    }

    /* istanbul ignore next */
    private DEBUG_debugCanvasDrawRegion(x: number, y: number): void {
        const dbgCanvasContext = this.ctx.debugCanvas.getContext("2d");
        dbgCanvasContext.fillStyle = "rgba(0, 255, 0, 255)";
        dbgCanvasContext.strokeStyle = "rgba(0, 0, 0, 0)";
        dbgCanvasContext.lineWidth = 0;
        let x1, y1, x2, y2;
        x1 = x;
        y1 = this.ctx.canvasBuffer.height - this.ctx.imageCapture.height - y;
        x2 = x1 + this.ctx.imageCapture.width - 1;
        y2 = y1 + this.ctx.imageCapture.height - 1;
        dbgCanvasContext.fillRect(x1, y1, this.ctx.imageCapture.width, 1);
        dbgCanvasContext.fillRect(x1, y2, this.ctx.imageCapture.width, 1);
        dbgCanvasContext.fillRect(x1, y1, 1, this.ctx.imageCapture.height);
        dbgCanvasContext.fillRect(x2, y1, 1, this.ctx.imageCapture.height);

        dbgCanvasContext.stroke();
        dbgCanvasContext.beginPath();
    }

    /* istanbul ignore next */
    private DEBUG_debugCanvasResize(): void {
        this.ctx.debugCanvas.width = this.ctx.canvasBuffer.width;
        this.ctx.debugCanvas.height = this.ctx.canvasBuffer.height;
    }

    private centerCanvas(): void {
        const outerCanvas = document.getElementById("canvasOuter");
        if (
            !outerCanvas ||
            this.ctx.canvas.element.style.display == "none" ||
            (outerCanvas.clientWidth == 0 || outerCanvas.clientHeight == 0)
        )
            return;

        (<any>this.ctx.canvas.element).needTranslate = true;
        if (
            outerCanvas.clientWidth * this.ctx.scaleFactor != this.ctx.canvas.element.width ||
            outerCanvas.clientHeight * this.ctx.scaleFactor != this.ctx.canvas.element.height
        ) {
            this.ctx.canvas.element.width = outerCanvas.clientWidth * this.ctx.scaleFactor;
            this.ctx.canvas.element.height = outerCanvas.clientHeight * this.ctx.scaleFactor;
        }

        let tmpX = this.centerCanvasHelper(this.ctx.canvas.element.width, this.ctx.canvas.width, this.ctx.targetX);
        this.ctx.left = tmpX.newOffset;

        let tmpY = this.centerCanvasHelper(this.ctx.canvas.element.height, this.ctx.canvas.height, this.ctx.targetY);
        this.ctx.top = tmpY.newOffset;

        let translateX = this.centerCanvasHelper(outerCanvas.clientWidth, this.ctx.canvas.width, this.ctx.targetX)
        this.ctx.translateX = translateX.targetValue;
        let translateY = this.centerCanvasHelper(outerCanvas.clientHeight, this.ctx.canvas.height, this.ctx.targetY)
        this.ctx.translateY = translateY.targetValue;
    }

    private centerCanvasHelper(clientSize: number, canvasSize: number, targetSize: number): any {
        /**
         * All these target related variables are used for the pan mode computation.
         * However, since we always rendering the full image of the device (with skin),
         * we only need to move the canvasOuter rather than the canvasObject, there is no
         * need to re-compute the offset after each time of movement.
         */

        let targetOffset = targetSize;
        let newOffset = (clientSize - canvasSize) / 2;
        let maxOffset = Math.abs(newOffset);
        targetOffset = Math.abs(targetOffset) > maxOffset ? maxOffset * Math.sign(targetOffset) : targetOffset;

        return { newOffset: newOffset, targetValue: targetOffset };
    }

    private drawSnapshotSkin = (snapshotCanvas: any): void => {
        DeviceScreenRenderingService.drawTransformedToCanvas(snapshotCanvas, this.ctx.imageSkin, 0, 0, this.ctx.rotation + this.ctx.clientSideRotation, this.ctx.scale);
    }

    private drawSnapshotImage = (snapshotCanvas: any): void => {
        if (this.ctx.deviceSupportsScreenProjection) {
            const rotation = this.ctx.rotation - this.ctx.targetRotation + this.ctx.clientSideRotation;
            const offset = MathHelpers.getProjectionOffset(this.ctx);
            DeviceScreenRenderingService.drawTransformedToFitSkin(snapshotCanvas, this.ctx.canvasBuffer, offset.x, offset.y, rotation, this.ctx.scale, this.ctx.skinScreenWidth, this.ctx.skinScreenHeight);
        } else {
            DeviceScreenRenderingService.drawTransformedToFitSkin(snapshotCanvas, this.ctx.canvasBuffer, this.ctx.screenOffsetX, this.ctx.screenOffsetY, this.ctx.rotation + this.ctx.clientSideRotation, this.ctx.scale, this.ctx.skinScreenWidth, this.ctx.skinScreenHeight);
        }
    }

    //public

    renderSkin(): void {
        // TODO 2025.1: optimize to call resize only when it's actually needed
        // mcrc.log.trace("Draw Skin - AutoZoomScale: " + autoZoomScale + " rotation: " + ctx.rotation + " scale: " + ctx.scale);

        // HC-5122 add skin for all the no skin devices to force the code go through the with-skin routine
        // since when 'Control without notification' is false, agent is not sending over the correct scale information
        if (this.ctx.imageSkin != null) {
            DeviceScreenRenderingService.resizeCanvasToTransformed(this.ctx.canvas, this.ctx.imageSkin, this.ctx.rotation + this.ctx.clientSideRotation, this.ctx.scale);
            this.centerCanvas();
            if (this.ctx.canvas.element.style.display !== "none" && this.ctx.clientScaleFactor !== 0 && this.ctx.imageSkin.emptySkin === undefined) {
                DeviceScreenRenderingService.drawTransformedToCanvas(this.ctx.canvas.element, this.ctx.imageSkin, 0, 0, this.ctx.rotation + this.ctx.clientSideRotation, this.ctx.scale);
            }
        }
    }

    renderImage(data: any): void {
        // TODO 2025.1: think of a better solution for handling deviceSupportsScreenProjection.
        // ctx.deviceSupportsScreenProjection flag if set will be there for the duration of the whole session
        const targetCanvas = this.ctx.canvasBuffer;
        if (this.ctx.canvas.element.style.display !== "none" && this.ctx.clientScaleFactor !== 0) {
            if (this.ctx.deviceSupportsScreenProjection) {
                const rotation = this.ctx.rotation - this.ctx.targetRotation + this.ctx.clientSideRotation;
                const offset = MathHelpers.getProjectionOffset(this.ctx);
                DeviceScreenRenderingService.drawTransformedToFitSkin(this.ctx.canvas.element, targetCanvas, offset.x, offset.y, rotation, this.ctx.scale, this.ctx.skinScreenWidth, this.ctx.skinScreenHeight);
            } else {
                DeviceScreenRenderingService.drawTransformedToFitSkin(this.ctx.canvas.element, targetCanvas, this.ctx.screenOffsetX, this.ctx.screenOffsetY, this.ctx.rotation + this.ctx.clientSideRotation, this.ctx.scale, this.ctx.skinScreenWidth, this.ctx.skinScreenHeight);
            }
        }
    }

    renderBuffer(data: any, fullscreen: boolean): void {
        if ((this.ctx.canvasBuffer.width != this.ctx.imageCapture.width || this.ctx.canvasBuffer.height != this.ctx.imageCapture.height) && fullscreen) {
            // TODO Jeff PR: resizing clears the entire content from the canvas, so we need some code to
            // preserve existing canvas content in temporary canvas before resizing and restore it after
            // TODO Jeff PR: Remove canvasBuffer or imageCapture once PC_DELTASCREEN only contains one image
            DeviceScreenRenderingService.resizeCanvasToTransformed(this.ctx.canvasBuffer, this.ctx.imageCapture, 0, 1);
            this.ctx.canvas.element.screenWidth = this.ctx.canvasBuffer.width;
            this.ctx.canvas.element.screenHeight = this.ctx.canvasBuffer.height;
            if (this.ctx.skinScreenWidth == -1) {
                this.ctx.skinScreenWidth = this.ctx.deviceWidth;
                this.ctx.skinScreenHeight = this.ctx.deviceHeight;
            }

            // if (this.ctx && this.ctx.imageCapture && this.ctx.canvas && this.ctx.imageSkin) {
            //     this.logger.info("Full screen update with image resolution: " + this.ctx.imageCapture.width + " x " + this.ctx.imageCapture.height
            //         + ", canvas size: " + this.ctx.canvas.element.clientWidth + " x " + this.ctx.canvas.element.clientHeight
            //         + ", calculated device screen: " + this.ctx.skinScreenWidth * this.ctx.scale + " x " + this.ctx.skinScreenHeight * this.ctx.scale
            //         + ", scale: " + this.ctx.scale
            //         + ", original device resolution: " + this.ctx.deviceWidth + " x " + this.ctx.deviceHeight
            //         + ", original skin resolution: " + this.ctx.imageSkin.width + " x " + this.ctx.imageSkin.height
            //         + ", original skinScreen resolution: " + this.ctx.skinScreenWidth + " x " + this.ctx.skinScreenHeight
            //         + ", quality: " + this.ctx.imageQuality
            //         + ", bitDepth: " + this.ctx.bitDepth
            //         + ", clientScaleFactor: " + this.ctx.clientScaleFactor);
            // }

            //DEBUG_debugCanvasResize(); // Uncomment to view region updates
        }
        // mcrc.log.trace("Draw Buffer - AutoZoomScale: " + autoZoomScale + " rotation: " + ctx.rotation + " scale: " + ctx.scale);
        //DEBUG_debugCanvasDrawRegion(data.x, data.y); // Uncomment to view region updates
        DeviceScreenRenderingService.drawTransformedToCanvas(this.ctx.canvasBuffer, this.ctx.imageCapture, data.x, this.ctx.canvasBuffer.height - this.ctx.imageCapture.height - data.y, 0, 1);
        //DEBUG_debugCanvasClearScreen();  // Uncomment to view region updates
    }

    overlay(): void {
        if (this.ctx.annotationsToolHint && this.ctx.cursorX && this.ctx.cursorY && this.ctx.annotationTool) {
            const c = this.ctx.canvas.element.getContext('2d');

            // Render hit-test screen boundary (keep for debugging?)
            //c.strokeStyle = 'rgb(255, 0, 0)';
            //c.lineWidth = 2.0;
            //c.beginPath();
            //const screenBounds = mcrc.utils.ui.computeScreenBounds(ctx);
            //c.rect(screenBounds.x, screenBounds.y, screenBounds.width, screenBounds.height);
            //c.stroke();

            switch (this.ctx.annotationTool) {
                case AnnotationTool.Pen:
                    c.fillStyle = this.ctx.annotationCursorColor || '#000000';
                    c.beginPath();
                    c.arc(this.ctx.cursorX, this.ctx.cursorY, ((this.ctx.annotationCursorSize || 10) / 2) * this.ctx.scale, 0, 2 * Math.PI, true);
                    c.fill();
                    break;
                case AnnotationTool.Eraser:
                    c.fillStyle = 'rgba(0, 0, 0, 0)';
                    c.strokeStyle = 'rgb(0, 0, 0)';
                    c.lineWidth = 2.0;
                    c.beginPath();
                    c.arc(this.ctx.cursorX, this.ctx.cursorY, ((this.ctx.annotationCursorSize || 10) / 2) * this.ctx.scale, 0, 2 * Math.PI, true);
                    c.stroke();
                    break;
            }
        }
    }

    init(): void {
        this.ctx = <DeviceContext>RcContext.instance.deviceContext;
        // Clear main canvas
        const screenCanvasContext = this.ctx.canvas.element.getContext("2d");
        screenCanvasContext.fillStyle = "white";
        screenCanvasContext.fillRect(0, 0, this.ctx.canvas.element.width, this.ctx.canvas.element.height);

        // Clear canvas buffer
        const canvasBufferContext = this.ctx.canvasBuffer.getContext("2d");
        canvasBufferContext.fillStyle = "white";
        canvasBufferContext.fillRect(0, 0, this.ctx.canvasBuffer.width, this.ctx.canvasBuffer.height);

        this.transformService = RcContext.instance.deviceScreenTransformService;
    }

    clear(): void {
        const screenCanvasContext = this.ctx.canvas.element.getContext("2d");
        screenCanvasContext.clearRect(0, 0, this.ctx.canvas.element.width, this.ctx.canvas.element.height);
        screenCanvasContext.restore();

        const canvasBufferContext = this.ctx.canvasBuffer.getContext("2d");
        canvasBufferContext.clearRect(0, 0, this.ctx.canvasBuffer.width, this.ctx.canvasBuffer.height);
        canvasBufferContext.restore();
    }

    getCanvasImage(bIncludeSkin: boolean): HTMLCanvasElement {
        let snapshotCanvas = document.createElement('canvas');
        if (!this.ctx.imageSkin && bIncludeSkin) {
            bIncludeSkin = false;
        }
        if (bIncludeSkin) {
            DeviceScreenRenderingService.resizeCanvasToTransformed(snapshotCanvas, this.ctx.imageSkin, this.ctx.rotation + this.ctx.clientSideRotation, this.ctx.scale);
            let snapshotCanvasContext = snapshotCanvas.getContext("2d");
            snapshotCanvasContext.fillStyle = "white";
            snapshotCanvasContext.fillRect(0, 0, snapshotCanvas.width, snapshotCanvas.height);

            let context = RcContext.instance.deviceContext as IDeviceContext;
            if (context.skin?.isIrregular) {
                this.drawSnapshotImage(snapshotCanvas);
                this.drawSnapshotSkin(snapshotCanvas);
            }
            else {
                this.drawSnapshotSkin(snapshotCanvas);
                this.drawSnapshotImage(snapshotCanvas);
            }
        } else {
            let rotation = this.ctx.deviceSupportsScreenProjection ?
                this.ctx.clientSideRotation :
                this.ctx.targetRotation + this.ctx.clientSideRotation;
            DeviceScreenRenderingService.resizeCanvasToTransformed(snapshotCanvas, this.ctx.canvasBuffer, rotation, 1);
            DeviceScreenRenderingService.drawTransformedToCanvas(snapshotCanvas, this.ctx.canvasBuffer, 0, 0, rotation, 1);
        }

        return snapshotCanvas;
    }

    getCanvasRotation(): number {
        return this.ctx.rotation;
    }

    deviceHasSkin(): boolean {
        return !this.ctx.imageSkin;
    }

    refresh2dCanvas(): void {
        return this.transformService.refreshCanvas();
    }
}

